import React, { useState, useEffect, useContext } from "react";
import {
  useTheme,
  Drawer,
  IconButton,
  Box,
  useMediaQuery,
  Typography,
  Button,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import MenuLateral from "../MenuLateral/MenuLateral";
import Content from "../Content/Content";
import Pagamento from "../pagamentos/Pagamento";
import Settings from "../Settings/Settings";
import Header from "../Header/Header";
import { UserContext } from "../userContext/UserContext";
import { motion, AnimatePresence } from "framer-motion";
import { hasPaidAccess } from "../../../utils/access";
import {
  registrarSalesEvent,
  setPaymentEntrySource,
} from "../../../utils/salesEvents.js";
import {
  getUnreadContentNotifications,
  markAllContentNotificationsAsRead,
  markContentNotificationAsRead,
} from "../notifications/contentNotifications";

const Painel = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentView, setCurrentView] = useState("inicio");
  const [userStatus, setUserStatus] = useState({
    isPaid: null,
    email: "",
    loading: true,
    paidAt: null,
    subscriptionExpiresAt: null,
    accessHold: null,
  });
  const [notifications, setNotifications] = useState([]);
  const { setUser } = useContext(UserContext);

  const [tourActive, setTourActive] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [highlightPosition, setHighlightPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });

  const tourSteps = [
    {
      target: isMobile ? ".menu-icon" : ".menu-lateral",
      message:
        "Bem-vindo à Future Trade! Siga nosso tour para melhor aproveitamento das ferramentas.",
    },
    {
      target: ".tab-instrucoes",
      message:
        "Aba Instruções: Aqui temos um passo a passo para você conseguir realizar a instalação dos nossos indicadores.",
    },
    {
      target: ".tab-videos",
      message:
        "Aba Vídeos: Aqui temos vídeos para lhe auxiliar na sua jornada (os 3 primeiros são essenciais para a instalação, siga a ordem; os outros são para aprimorar seu conhecimento).",
    },
    {
      target: ".tab-arquivos",
      message:
        "Aba Arquivos: Nesta aba, todos os meses você receberá atualizações sobre os indicadores. No início de cada mês, venha aqui e faça o download das suas atualizações.",
    },
    {
      target: ".tab-termometro",
      message:
        "Aba Termômetro Financeiro: Aqui temos um gadget para análise das tendências dos mercados de Mini Índice e Mini Dólar.",
    },
  ];
  const assinaturaVencida =
    userStatus.subscriptionExpiresAt &&
    new Date(userStatus.subscriptionExpiresAt) < new Date();

  const usuarioPago = hasPaidAccess(userStatus.isPaid);
  const nuncaPagou = !usuarioPago && !userStatus.subscriptionExpiresAt;

  const accessHold = userStatus.accessHold;

  const handlePanelViewChange = (view) => {
    if (view === "pagamento") {
      setPaymentEntrySource("subscription_menu");
    }
    setCurrentView(view);
  };

  const formatDateTimeBR = (value) => {
    if (!value) return "N/A";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";

    return date.toLocaleString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      hour12: false,
    });
  };

  const formatCurrencyBRL = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "N/A";

    return amount.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Você não está autenticado. Redirecionando para login...");
        window.location.href = "/login";
        return;
      }

      try {
        const userResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/auth/user-status`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const userData = await userResponse.json();

        if (!userResponse.ok) {
          if (userResponse.status === 401 || userResponse.status === 403) {
            localStorage.removeItem("token");
            localStorage.removeItem("role");
            window.location.href = "/login";
            return;
          }

          throw new Error(userData.message);
        }

        setUserStatus({
          userId: userData.userId,
          isPaid: userData.isPaid,
          fullName: userData.fullName,
          email: userData.email,
          phone: userData.phone,
          loading: false,
          paidAt: userData.paidAt,
          subscriptionExpiresAt: userData.subscriptionExpiresAt,
          accessHold: userData.accessHold || null,
        });
        setUser({
          id: userData.userId,
          fullName: userData.fullName,
          email: userData.email,
          phone: userData.phone,
          role: userData.role,
          isPaid: userData.isPaid,
          paidAt: userData.paidAt,
          subscriptionExpiresAt: userData.subscriptionExpiresAt,
          accessHold: userData.accessHold || null,
        });

        try {
          const tourResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/auth/tour-status`,
            {
              method: "GET",
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const tourData = await tourResponse.json();
          if (tourResponse.ok && !tourData.tourCompleted) {
            setTourActive(true);
          }
        } catch (tourError) {
          console.warn("Não foi possível carregar status do tour:", tourError);
        }
      } catch (error) {
        console.error("Erro ao carregar status do usuário:", error);
        setUserStatus((prev) => ({ ...prev, loading: false }));
        alert("Erro ao carregar seus dados. Atualize a página e tente novamente.");
      }
    };

    fetchUserData();
  }, [setUser]);

  useEffect(() => {
    if (!userStatus.userId || userStatus.loading) return;

    registrarSalesEvent(userStatus.userId, "panel_session_start", {
      source: "user_panel",
      metadata: { path: window.location.pathname },
    });
  }, [userStatus.userId, userStatus.loading]);

  // Atualiza a posição do highlight sempre que o tourStep mudar
  useEffect(() => {
    const updateHighlightPosition = () => {
      const targetElement = document.querySelector(tourSteps[tourStep].target);
      if (!targetElement) {
        setHighlightPosition({ top: 0, left: 0, width: 0, height: 0 });
        return;
      }

      // Rola a página até o elemento para garantir que ele esteja visível
      targetElement.scrollIntoView({ behavior: "smooth", block: "center" });

      const rect = targetElement.getBoundingClientRect();

      setHighlightPosition({
        top: rect.top - 10,
        left: rect.left - 10,
        width: rect.width + 20,
        height: rect.height + 20,
      });
    };

    if (tourActive) {
      // Adiciona um atraso para garantir que o elemento esteja renderizado
      const timer = setTimeout(updateHighlightPosition, 300);
      return () => clearTimeout(timer);
    }
  }, [tourStep, tourActive]);

  useEffect(() => {
    if (!tourActive) return;

    const selector = tourSteps[tourStep]?.target;
    const el = document.querySelector(selector);

    if (!el) return;

    el.classList.add("tour-focus");

    return () => {
      el.classList.remove("tour-focus");
    };
  }, [tourStep, tourActive]);

  const completeTour = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/auth/mark-tour-completed`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        setTourActive(false);
      }
    } catch (error) {
      console.error("Erro ao marcar tour como concluído:", error);
    }
  };

  const handleNext = () => {
    if (tourStep < tourSteps.length - 1) {
      setTourStep(tourStep + 1);
    } else {
      completeTour();
    }
  };

  const handlePrevious = () => {
    if (tourStep > 0) setTourStep(tourStep - 1);
  };

  const renderContent = () => {
    const userId = userStatus.userId;
    switch (currentView) {
      case "inicio":
        return (
          <Content
            isPaid={userStatus.isPaid}
            setCurrentView={setCurrentView}
            tourActive={tourActive}
            tourStep={tourStep}
          />
        );
      case "pagamento":
        return (
          <Pagamento
            userId={userId}
            onExit={() => setCurrentView("inicio")}
          />
        );
      case "configuracoes":
        return <Settings />;
      default:
        return <Content isPaid={userStatus.isPaid} />;
    }
  };

  useEffect(() => {
    if (!userStatus.userId) return;
    setNotifications(getUnreadContentNotifications(userStatus.userId));
  }, [userStatus.userId]);

  const handleNotificationClick = (notification) => {
    markContentNotificationAsRead(userStatus.userId, notification.id);
    setNotifications((prev) => prev.filter((item) => item.id !== notification.id));

    if (notification.target === "videos") {
      setCurrentView("inicio");
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("userpanel:navigate-tab", { detail: { tab: "videos" } })
        );
      }, 0);
    }
  };

  const handleMarkAllNotificationsAsRead = () => {
    markAllContentNotificationsAsRead(userStatus.userId, notifications);
    setNotifications([]);
  };

  const getTooltipPosition = (step) => {
    if (isMobile) {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }

    if (step === 0) {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }

    const targetElement = document.querySelector(tourSteps[step].target);
    if (!targetElement) {
      return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    }

    const rect = targetElement.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    let top = rect.bottom + 20;
    let left = rect.left + rect.width / 2;

    // Ajusta a posição para não cobrir o elemento destacado
    // Se o elemento estiver muito embaixo, coloca o tooltip acima
    if (top + 200 > windowHeight) {
      top = rect.top - 200;
    }

    // Se o elemento estiver muito em cima, coloca o tooltip embaixo
    if (top < 50) {
      top = rect.bottom + 20;
    }

    // Ajusta horizontalmente para não sair da tela
    if (left + 450 > windowWidth) {
      left = windowWidth - 450 - 20;
    } else if (left - 450 / 2 < 0) {
      left = 20 + 450 / 2;
    }

    return {
      top: `${top}px`,
      left: `${left}px`,
      transform: "translateX(-50%)",
    };
  };

  if (userStatus.loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#0D1B2A",
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          style={{
            width: 60,
            height: 60,
            border: "4px solid #00d4ff",
            borderTopColor: "transparent",
            borderRadius: "50%",
          }}
        />
      </Box>
    );
  }

  if (assinaturaVencida && currentView !== "pagamento") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#0D1B2A",
        }}
      >
        <Box
          sx={{
            maxWidth: 420,
            width: "100%",
            p: 4,
            borderRadius: 3,
            textAlign: "center",
            bgcolor: "#1B263B",
            boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            border: "1px solid rgba(0, 212, 255, 0.2)",
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ color: "#FFFFFF" }}>
            Seu acesso expirou ⏳
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: "rgba(255, 255, 255, 0.85)", mb: 3 }}
          >
            Para continuar usando todas as ferramentas da Future Trade, renove
            sua assinatura agora.
          </Typography>

          <Button
            size="large"
            variant="contained"
            fullWidth
            onClick={() => {
              if (userStatus.userId) {
                registrarSalesEvent(userStatus.userId, "cta_subscribe_click", {
                  source: "user_panel",
                  metadata: { location: "painel_renovar_acesso" },
                });
              }
              setPaymentEntrySource("expired_access_gate");
              setCurrentView("pagamento");
            }}
            sx={{
              background: "linear-gradient(90deg, #00d4ff 0%, #1B263B 100%)",
              color: "#FFFFFF",
              fontWeight: 600,
              "&:hover": {
                background: "linear-gradient(90deg, #1B263B 0%, #00d4ff 100%)",
              },
            }}
          >
            Renovar acesso
          </Button>

          <Typography
            variant="caption"
            sx={{ display: "block", mt: 2, color: "rgba(255, 255, 255, 0.6)" }}
          >
            Acesso liberado imediatamente após o pagamento
          </Typography>
        </Box>
      </Box>
    );
  }

  if (accessHold?.active) {
    const summary = accessHold.summary || {};

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 4,
          bgcolor: "#08131F",
          background:
            "radial-gradient(circle at top, rgba(197, 86, 86, 0.18) 0%, rgba(8,19,31,1) 55%)",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 820,
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            bgcolor: "rgba(12, 21, 34, 0.94)",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
          }}
        >
          <Typography
            variant="h4"
            sx={{ color: "#FFF4F4", fontWeight: 800, mb: 1 }}
          >
            Conta temporariamente em análise
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              mb: 2.5,
              px: 1.6,
              py: 1.2,
              borderRadius: 2,
              bgcolor: "rgba(255, 193, 7, 0.12)",
              border: "1px solid rgba(255, 193, 7, 0.24)",
            }}
          >
            <WarningAmberRoundedIcon sx={{ color: "#FFD54F", fontSize: 28 }} />
            <Typography sx={{ color: "#FFE082", fontWeight: 700 }}>
              Pedido de reembolso registrado em{" "}
              {formatDateTimeBR(summary.refundRequestedAt)}
            </Typography>
          </Box>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.82)",
              fontSize: "1rem",
              lineHeight: 1.7,
              mb: 3,
            }}
          >
            Seu login foi confirmado, mas o uso da ferramenta está suspenso
            enquanto avaliamos uma solicitação de reembolso com indício de
            atividade suspeita.
          </Typography>

          <Box
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2.5,
              bgcolor: "rgba(176, 56, 56, 0.14)",
              border: "1px solid rgba(255, 132, 132, 0.18)",
            }}
          >
            <Typography sx={{ color: "#FFC6C6", fontWeight: 700, mb: 0.5 }}>
              Motivo da trava
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.84)" }}>
              {accessHold.reason}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2.2,
              borderRadius: 2.5,
              bgcolor: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <Typography
              sx={{
                color: "#F6FAFF",
                fontWeight: 800,
                mb: 1.2,
              }}
            >
              Resumo da análise
            </Typography>

            <Box
              component="ul"
              sx={{
                m: 0,
                pl: 2.5,
                display: "grid",
                gap: 0.9,
                color: "rgba(255,255,255,0.86)",
              }}
            >
              {[
                `Usuário: ${summary.fullName || "N/A"}`,
                `E-mail: ${summary.email || userStatus.email || "N/A"}`,
                `Pedido de reembolso: ${formatDateTimeBR(
                  summary.refundRequestedAt
                )}`,
                `Data do cadastro: ${formatDateTimeBR(summary.registeredAt)}`,
                `Data do pagamento: ${formatDateTimeBR(summary.paidAt)}`,
                `Último pagamento aprovado: ${
                  summary.latestPaidAmount != null
                    ? `${formatCurrencyBRL(summary.latestPaidAmount)}${
                        summary.latestPaidMethod
                          ? ` via ${summary.latestPaidMethod}`
                          : ""
                      }`
                    : "N/A"
                }`,
                `Assinatura válida até: ${formatDateTimeBR(
                  summary.subscriptionExpiresAt
                )}`,
                `Total de downloads: ${String(summary.totalDownloads ?? 0)}`,
                `Downloads nos últimos 30 dias: ${String(
                  summary.downloadsLast30Days ?? 0
                )}`,
                `Último download: ${formatDateTimeBR(summary.lastDownloadAt)}`,
                `Trava aplicada em: ${formatDateTimeBR(accessHold.createdAt)}`,
              ].map((item) => (
                <Box
                  component="li"
                  key={item}
                  sx={{
                    lineHeight: 1.6,
                  }}
                >
                  {item}
                </Box>
              ))}
            </Box>
          </Box>

          <Typography
            sx={{
              mt: 3,
              color: "rgba(255,255,255,0.68)",
              lineHeight: 1.7,
            }}
          >
            O acesso será liberado somente após a conclusão da análise interna.
            Até lá, downloads, indicadores e demais recursos permanecem
            bloqueados.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        bgcolor: "#0D1B2A",
      }}
    >
      {isMobile ? (
        <>
          <IconButton
            onClick={tourActive ? null : () => setIsDrawerOpen(true)}
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
              zIndex: 1200,
              color: "#00d4ff",
              backgroundColor: "#1B263B",
              "&:hover": {
                backgroundColor: "#2a3a52",
              },
            }}
            className="menu-icon"
            onMouseDown={(e) => {
              if (tourActive) {
                e.preventDefault();
                e.stopPropagation();
              
              }
            }}
          >
            <MenuIcon />
          </IconButton>
          <Drawer
            anchor="left"
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: "80vw",
                maxWidth: "340px",
                minWidth: "260px",
                height: "100vh",
                overflowY: "auto",
                p: 0,
                m: 0,
                bgcolor: "transparent",
                borderRadius: 0,
                boxShadow: "none",
              },
            }}
          >
            <MenuLateral
              className="menu-lateral"
              setCurrentView={(view) => {
                handlePanelViewChange(view);
                setIsDrawerOpen(false);
              }}
              handleClose={() => setIsDrawerOpen(false)}
            />
          </Drawer>
        </>
      ) : (
        <Box
          sx={{
            width: "280px",
            flexShrink: 0,
            bgcolor: "#1B263B",
            height: "100vh",
            overflowY: "auto",
          }}
        >
          <MenuLateral
            className="menu-lateral"
            setCurrentView={handlePanelViewChange}
          />
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          padding: isMobile ? 0 : 2,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#0D1B2A",
          pointerEvents: tourActive && nuncaPagou ? "none" : "auto",
        }}
      >
        <Header
          className="header-notifications"
          notifications={notifications}
          onNotificationClick={handleNotificationClick}
          onMarkAllRead={handleMarkAllNotificationsAsRead}
        />
        <Box
          className="inicio-content"
          sx={{ flex: 1, width: "100%", mt: isMobile ? 0 : 2 }}
        >
          {renderContent()}
        </Box>
      </Box>

      {tourActive && nuncaPagou && (
        <>
          {/* Overlay escuro (seu fundo antigo) */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(13, 27, 42, 0.9)",
              zIndex: 1300,
              pointerEvents: "none",
              maskImage: `
      radial-gradient(
        circle at
        ${highlightPosition.left + highlightPosition.width / 2}px
        ${highlightPosition.top + highlightPosition.height / 2}px,
        transparent ${
          Math.max(highlightPosition.width, highlightPosition.height) / 2 + 20
        }px,
        black ${
          Math.max(highlightPosition.width, highlightPosition.height) / 2 + 40
        }px
      )
    `,
              WebkitMaskImage: `
      radial-gradient(
        circle at
        ${highlightPosition.left + highlightPosition.width / 2}px
        ${highlightPosition.top + highlightPosition.height / 2}px,
        transparent ${
          Math.max(highlightPosition.width, highlightPosition.height) / 2 + 20
        }px,
        black ${
          Math.max(highlightPosition.width, highlightPosition.height) / 2 + 40
        }px
      )
    `,
            }}
          />

          {/* Highlight com efeito de brilho e transparência */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{
              opacity: 1,
              scale: 1,
              ...highlightPosition,
            }}
            transition={{
              opacity: { duration: 0.5 },
              scale: { duration: 0.5 },
            }}
            style={{
              position: "absolute",
              border: "3px solid #00d4ff",
              borderRadius: "12px",
              zIndex: 1400,
              pointerEvents: "none",
              boxShadow: `
              0 0 20px rgba(0, 212, 255, 0.7),
              inset 0 0 20px rgba(0, 212, 255, 0.3)
            `,
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          />

          {/* Tooltip do tour - Corrigindo posição */}
          <AnimatePresence mode="wait">
            <motion.div
              key={tourStep}
              initial={{
                opacity: 0,
                scale: 0.9,
                ...getTooltipPosition(tourStep),
              }}
              animate={{
                opacity: 1,
                scale: 1,
                ...getTooltipPosition(tourStep),
              }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              style={{
                position: "fixed",
                background: "linear-gradient(135deg, #1B263B 0%, #2a3a52 100%)",
                padding: isMobile ? "16px" : "20px",
                borderRadius: "12px",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.8)",
                zIndex: 1500,
                maxWidth: isMobile ? "300px" : "450px",
                textAlign: "center",
                border: "1px solid rgba(0, 212, 255, 0.3)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#FFFFFF",
                  fontWeight: 500,
                  lineHeight: 1.4,
                  mb: 3,
                  fontSize: isMobile ? "16px" : "18px",
                  textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                }}
              >
                {tourSteps[tourStep].message}
              </Typography>

              {/* Indicador de progresso */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                {tourSteps.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor:
                        index === tourStep
                          ? "#00d4ff"
                          : "rgba(255, 255, 255, 0.2)",
                      transition: "all 0.3s ease",
                    }}
                  />
                ))}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Button
                  disabled={tourStep === 0}
                  onClick={handlePrevious}
                  sx={{
                    color: "#00d4ff",
                    border: "1px solid #00d4ff",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    fontWeight: 500,
                    fontSize: "14px",
                    minWidth: "100px",
                    "&:hover": {
                      backgroundColor: "rgba(0, 212, 255, 0.1)",
                    },
                    "&:disabled": {
                      color: "rgba(255, 255, 255, 0.3)",
                      borderColor: "rgba(255, 255, 255, 0.3)",
                    },
                  }}
                >
                  Anterior
                </Button>
                <Button
                  onClick={handleNext}
                  sx={{
                    color: "#FFFFFF",
                    background:
                      "linear-gradient(90deg, #00d4ff 0%, #0088ff 100%)",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    fontWeight: 500,
                    fontSize: "14px",
                    minWidth: "100px",
                    "&:hover": {
                      background:
                        "linear-gradient(90deg, #0088ff 0%, #00d4ff 100%)",
                      boxShadow: "0 0 10px rgba(0, 212, 255, 0.5)",
                    },
                  }}
                >
                  {tourStep === tourSteps.length - 1 ? "Concluir" : "Próximo"}
                </Button>
              </Box>
            </motion.div>
          </AnimatePresence>
        </>
      )}
    </Box>
  );
};

export default Painel;
